import { useEffect, useState } from "react";
import { Loading, Step, StepLabel, Stepper } from "../../components";
import { Minimal } from "../../layouts";
import { ImportarXmlActions, ImportarXmlStepper } from "./components";
import {
  cadastroProdutosDto,
  calculaSubtotaisProdutos,
  encontrarProduto,
  enderecoFornecedor,
  extrairDadosNFe,
  ordenarPorOrdem,
  parseXml,
} from "./importar-xml";
import {
  CadastroNfEntradaXml,
  FormatNfe,
  FormatProdutosCadastro,
  FormatXmlProdutos,
} from "./entities";
import { useImportContext } from "../../contexts/import.context";
import {
  CfopService,
  EnderecoService,
  history,
  MeioPagamentoService,
  NcmService,
  NfEntradaService,
  notification,
  OrigemMercadoriaService,
  PessoaService,
  ProdutosService,
  SessionService,
  UnidadeComercialService,
} from "../../services";
import {
  formatDate,
  responseErros,
  filtroNfEntradaExistente,
  nfEntradaJaLancada,
} from "../../utils";
import { FormatCfopHelper, InputErros } from "../../helpers";
import nfEntradaItensXmlValidator, {
  nfEntradaItensXmlExcetoUsoConsumoValidator,
} from "./middlewares/nf-entrada-itens-xml.validator";
import shortid from "shortid";
import { addDays } from "date-fns";

const cfopHelper = new FormatCfopHelper();
const nfEntradaService = new NfEntradaService();
const unidadeComercialService = new UnidadeComercialService();
const origemMercadoriaService = new OrigemMercadoriaService();
const produtosService = new ProdutosService();
const ncmService = new NcmService();
const cfopService = new CfopService();
const meioPagamentoService = new MeioPagamentoService();
const enderecoService = new EnderecoService();
const pessoaService = new PessoaService();
const sessionService = new SessionService();

const ImportarXmlView = () => {
  const { nfEntradaDto, setNfEntradaDto } = useImportContext();
  const [activeStep, setActiveStep] = useState(0);
  const [arquivoXml, setArquivoXml] = useState(window?.arquivoXml);
  const [dadosXml, setDadosXml] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [produtosNaoCadastrados, setProdutosNaoCadastrados] = useState([]);
  const [fornecedor, setFornecedor] = useState(null);
  const [transportadora, setTransportadora] = useState(null);
  const [listaUnidadesComerciais, setListaUnidadesComerciais] = useState([]);
  const [origensMercadorias, setOrigensMercadorias] = useState([]);
  const [isCadastro, setIsCadastro] = useState(false);
  const [inputErro, setInputErro] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputErros = new InputErros(inputErro, setInputErro);
  const dadosEmpresa = sessionService.getEmpresa();

  const steps = [
    { titulo: "Arquivo XML", funcaoAcao: buscarXmlData },
    { titulo: "Fornecedor", funcaoAcao: buscarTransportadora },
    { titulo: "Transportadora", funcaoAcao: formatarProdutos },
    { titulo: "Cadastro Produtos", funcaoAcao: cadastrarProdutos },
    { titulo: "Produtos", funcaoAcao: finalizarImportacao },
  ];

  useEffect(() => {
    buscarUnidadesComerciais();
    buscarOrigensMercadoria();
    if (window?.arquivoXml) {
      initDadosXml();
      delete window.arquivoXml;
    }
  }, []);

  const initDadosXml = async () => {
    try {
      await buscarXmlData();
    } catch (error) {
      setArquivoXml(null);
      notification.alertaGenericos(
        error.message || "Ocorreu um erro inesperado.",
      );
      return;
    }

    setActiveStep(1);
  };

  const buscarUnidadesComerciais = () => {
    unidadeComercialService.getAll().then((res) => {
      if (!res.isAxiosError) {
        setListaUnidadesComerciais(res.data);
      } else {
        responseErros(res);
      }
    });
  };

  const buscarOrigensMercadoria = () => {
    origemMercadoriaService.getAll().then((res) => {
      if (!res.isAxiosError) {
        setOrigensMercadorias(res.data);
      } else {
        responseErros(res);
      }
    });
  };

  const validarCnpjResponsavel = (informacoesXml) => {
    const cnpjEmpresa = dadosEmpresa.cnpjCpf;
    const extrairCnpjCpf = (obj) => obj.CNPJ || obj.CPF;
    const emitCnpj = extrairCnpjCpf(informacoesXml?.fornecedor),
      destCnpj = extrairCnpjCpf(informacoesXml?.destinatario),
      autCnpj = informacoesXml?.autorizadores.map((aut) => extrairCnpjCpf(aut));

    if (
      emitCnpj === cnpjEmpresa ||
      destCnpj === cnpjEmpresa ||
      autCnpj.includes(cnpjEmpresa)
    ) {
      return true;
    }

    throw new Error("Rejeição - Nota não possui referência à empresa");
  };

  const handleFileRead = async (e) => {
    try {
      const content = e.target.result;
      const xmlData = parseXml(content.replace(/>\s+</g, "><").trim());
      const dadosExtraidos = extrairDadosNFe(xmlData, handleInitStep);
      validarCnpjResponsavel(dadosExtraidos);
      await formatNFeData(dadosExtraidos);
    } catch (error) {
      throw new Error(error.message);
    }
  };

  async function buscarXmlData() {
    try {
      if (!arquivoXml) {
        throw new Error("Por favor, inserir XML referente a NF-e");
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = async (e) => {
          try {
            await handleFileRead(e);
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error("Erro ao ler o arquivo XML"));
        reader.readAsText(arquivoXml);
      });
    } catch (error) {
      throw new Error(error.message);
    }
  }

  async function formatNFeData(data) {
    const dtoNfe = new FormatNfe({
      ...data.identificacao,
      ...data.protocolo,
      ...data.transporte,
      ...data.totais,
      ...data.totaisIBSCBS,
      fornecedor: data.fornecedor,
      informacoes: data.informacoes,
      itens: data.itens,
      pagamentos: data.pagamentos,
      duplicatas: data.duplicatas,
    });
    try {
      await buscarFornecedores(dtoNfe);
    } catch (error) {
      throw new Error(error.message);
    }
    setDadosXml(dtoNfe);
  }

  async function buscarFornecedores(xml) {
    try {
      const filtro = {
        cnpjCpf: xml?.fornecedor?.cnpjCpf,
      };
      const result = await pessoaService.getAll(filtro);
      if (!result.isAxiosError) {
        if (result.data?.rows.length) {
          const fornecedorData = result.data.rows[0];
          try {
            await buscarNfEntradaExistente(fornecedorData?.id, xml);
          } catch (error) {
            throw new Error(error.message);
          }
          setFornecedor({
            ...fornecedorData,
            endereco: enderecoFornecedor(fornecedorData?.enderecos[0]),
          });
        } else {
          setIsCadastro(true);
          setFornecedor({
            apelidoFantasia: xml?.fornecedor?.apelidoFantasia,
            naturezaSocial: xml?.fornecedor?.naturezaSocial,
            cnpjCpf: xml?.fornecedor?.cnpjCpf,
            endereco: enderecoFornecedor(xml?.fornecedor?.endereco),
          });
        }
      } else {
        throw new Error(result.response?.data?.message);
      }
    } catch (error) {
      throw new Error(error.message);
    }
  }

  async function cadastrarFornecedor() {
    const fornecedor = dadosXml?.fornecedor;
    const body = {
      ...fornecedor,
      fornecedor: {},
      enderecos: fornecedor?.endereco
        ? [await enderecoFornecedorCidadeId(fornecedor.endereco)]
        : [],
    };
    setLoading(true);
    const result = await pessoaService.cadastrarPessoaConfirmacao(body);
    if (!result.isAxiosError) {
      setFornecedor({ ...fornecedor, ...result.data });
      setIsCadastro(false);
      setLoading(false);
      notification.cadastroSucesso();
    } else {
      setLoading(false);
      responseErros(result);
      throw new Error(result.response?.data?.message);
    }
  }

  async function enderecoFornecedorCidadeId(endereco) {
    const result = await enderecoService.getCidadeByCodigo(
      endereco?.codigoCidade,
    );
    if (!result.isAxiosError) {
      return { ...endereco, cidadeId: result.data.id };
    } else {
      responseErros(result);
    }
    return endereco;
  }

  async function buscarNfEntradaExistente(fornecedorId, xml) {
    if (xml?.numeroNota && xml?.serie) {
      const result = await nfEntradaService.getAllFiltroAvancado(
        filtroNfEntradaExistente(fornecedorId, xml.numeroNota, xml.serie),
      );
      if (!result.isAxiosError) {
        if (nfEntradaJaLancada(result.data, xml.numeroNota, xml.serie)) {
          throw new Error(
            "Uma nota de entrada com mesmo número e série desse fornecedor já foi lançada anteriormente.",
          );
        }
      } else {
        throw new Error(result.response?.data?.message);
      }
    }
  }

  async function buscarTransportadora() {
    try {
      if (!dadosXml?.transportadora) return;
      const filtro = {
        cnpjCpf: dadosXml?.transportadora?.cnpjCpf,
      };
      const result = await pessoaService.getAll(filtro);
      if (!result.isAxiosError) {
        if (result.data?.rows.length) {
          setTransportadora({
            ...result.data.rows[0],
            endereco: result.data.rows[0].transportadoras[0],
          });
        } else {
          setTransportadora(dadosXml.transportadora);
          setIsCadastro(true);
        }
      } else {
        throw new Error(
          result.response?.data?.message || "Erro ao buscar transportadora",
        );
      }
    } catch (error) {
      throw new Error(error.message);
    }
  }

  async function cadastrarTransportadora() {
    try {
      const transportadora = dadosXml?.transportadora;
      const body = {
        ...transportadora,
        transportadora: transportadora?.endereco,
      };
      setLoading(true);
      const result = await pessoaService.cadastrarPessoaConfirmacao(body);
      if (!result.isAxiosError) {
        setTransportadora({ ...transportadora, ...result.data });
        setIsCadastro(false);
        setLoading(false);
        notification.cadastroSucesso();
      } else {
        setLoading(false);
        responseErros(result);
        throw new Error(result.response?.data?.message);
      }
    } catch (error) {
      throw new Error(error.message);
    }
  }

  async function formatarProdutos() {
    try {
      const codigosProdutos = new Set();
      const cfopsProdutos = new Set();
      const codigosBarraProdutos = new Set();
      dadosXml?.itens.forEach((item) => {
        codigosProdutos.add(item.prod?.NCM);
        cfopsProdutos.add(cfopHelper.contraPartida(item.prod?.CFOP));
        if (
          item.prod?.cEAN &&
          item.prod?.cEAN !== "SEM GTIN" &&
          typeof item.prod?.cEAN !== "object"
        ) {
          codigosBarraProdutos.add(item.prod.cEAN);
        }
      });
      const [listaNcm, listaCfops, produtosFornecedores, produtosBarra] =
        await Promise.all([
          buscarNcms(Array.from(codigosProdutos)),
          buscarCfops(Array.from(cfopsProdutos)),
          buscarProdutosFornecedores(fornecedor?.id),
          buscarProdutos(Array.from(codigosBarraProdutos)),
        ]);
      const [cadastrados, naoCadastrados] = [[], []];
      dadosXml?.itens.forEach((item, index) => {
        const produto = new FormatXmlProdutos(
          { ...item.prod, ...item.imposto, nItem: item.nItem },
          fornecedor?.id,
          listaNcm,
          listaCfops,
          listaUnidadesComerciais,
          origensMercadorias,
          nfEntradaDto,
        );
        const itemEncontrado = encontrarProduto(
          produto,
          produtosFornecedores,
          produtosBarra,
        );
        if (itemEncontrado) {
          cadastrados.push(
            cadastroProdutosDto(
              {
                ...produto,
                ...itemEncontrado,
                ...(itemEncontrado?.informacao || {}),
                produto: itemEncontrado.descricao,
                produtoId: itemEncontrado.id,
                produtoInformacaoId: itemEncontrado?.informacao?.id,
                codigoProduto: itemEncontrado.codigoProduto,
                aliquotaMva: produto?.aliquotaMva,
                produtoCadastrado: true,
              },
              index,
            ),
          );
        } else {
          naoCadastrados.push(cadastroProdutosDto(produto, index));
        }
      });
      setProdutos(cadastrados);
      setProdutosNaoCadastrados(naoCadastrados);
      if (naoCadastrados.length > 0) {
        setIsCadastro(true);
      }
      return naoCadastrados.length === 0;
    } catch (error) {
      throw new Error(error.message || "Erro ao formatar produtos");
    }
  }

  const buscarNcms = async (codigo) => {
    const filtros = {
      codigo,
      nonPaginated: true,
    };
    if (codigo.length) {
      const result = await ncmService.getAllAvancado(filtros);
      if (!result.isAxiosError) {
        return result.data;
      } else {
        responseErros(result);
        throw new Error(
          result.response?.data?.message || "Erro ao buscar NCMs",
        );
      }
    }
  };

  const buscarCfops = async (codigo) => {
    const filtros = {
      codigo,
      nonPaginated: true,
    };
    if (codigo.length) {
      const result = await cfopService.getAllAvancado(filtros);
      if (!result.isAxiosError) {
        return result.data;
      } else {
        responseErros(result);
        throw new Error(
          result.response?.data?.message || "Erro ao buscar CFOPs",
        );
      }
    }
  };

  const buscarProdutos = async (codigosBarra) => {
    if (!codigosBarra || !codigosBarra?.length) return [];
    const result = await produtosService.getAllProdutosSimples({
      codigoBarra: codigosBarra,
      ativado: true,
      nonPaginated: true,
    });
    if (!result.isAxiosError) {
      return result.data;
    } else {
      responseErros(result);
      throw new Error(
        result.response?.data?.message ||
          "Erro ao buscar produtos por código barras",
      );
    }
  };

  const buscarProdutosFornecedores = async (fornecedorId) => {
    if (!fornecedorId) return [];
    const result = await produtosService.getAllProdutosFornecedores({
      fornecedorId,
      nonPaginated: true,
    });
    if (!result.isAxiosError) {
      return result.data.map((produtoFornecedor) => ({
        ...produtoFornecedor.produto,
        codigoProduto: produtoFornecedor.codigoProduto,
      }));
    } else {
      responseErros(result);
      throw new Error(
        result.response?.data?.message ||
          "Erro ao buscar produtos do fornecedor",
      );
    }
  };

  async function cadastrarProdutos() {
    try {
      if (!produtosNaoCadastrados.length) return;
      const produtosParaCadastro = produtosNaoCadastrados.filter(
        (item) => !item.usoConsumo,
      );
      const produtosUsoConsumo = produtosNaoCadastrados.filter(
        (item) => item.usoConsumo,
      );
      if (produtosParaCadastro.length === 0) {
        const produtosTemp = produtosUsoConsumo.map((item) => ({
          ...item,
          registrado: true,
        }));
        setProdutos((prevState) =>
          ordenarPorOrdem([...prevState, ...produtosTemp]),
        );
      } else {
        try {
          await nfEntradaItensXmlValidator.validate(produtosNaoCadastrados, {
            abortEarly: false,
          });
          const codigosProdutos = new Set();
          const produtosUnicos = [];
          const produtosRepetidos = [];
          for (const item of produtosParaCadastro) {
            if (codigosProdutos.has(item.codigoProduto)) {
              produtosRepetidos.push(item);
            } else {
              codigosProdutos.add(item.codigoProduto);
              produtosUnicos.push(item);
            }
          }
          const produtosCadastroDto = produtosUnicos.map(
            (produto) => new FormatProdutosCadastro(produto),
          );
          const result = await produtosService.cadastrarListaProdutos(
            produtosCadastroDto,
          );
          if (!result.isAxiosError) {
            mapearProdutosCadastrados(
              result?.data,
              produtosUsoConsumo,
              produtosRepetidos,
            );
            setProdutosNaoCadastrados(produtosUsoConsumo);
            setIsCadastro(false);
          } else {
            throw new Error(result.response?.data?.message);
          }
        } catch (err) {
          inputErros.set(err);
          responseErros(err);
          throw new Error(
            err?.errors[0]
              ? `${err.errors[0]} é obrigatório`
              : "Erro de validação",
          );
        }
      }
    } catch (error) {
      throw new Error(error.message);
    }
  }

  const mapearProdutosCadastrados = (
    itensDoBanco,
    produtosUsoConsumo,
    produtosRepetidos,
  ) => {
    let produtosTemp = [];
    if (produtosUsoConsumo.length > 0) {
      produtosTemp = produtosUsoConsumo.map((item) => ({
        ...item,
        registrado: true,
      }));
    }
    const produtosCadastrados = itensDoBanco.map((produto) => {
      const codigoProduto = produto.fornecedores.find(
        ({ fornecedorId }) => fornecedorId === fornecedor.id,
      )?.codigoProduto;
      const itemEncontrado = encontrarProduto(
        { ...produto, codigoProduto },
        produtosNaoCadastrados,
      );
      return {
        ...itemEncontrado,
        informacao: produto.informacao,
        produtoInformacaoId: produto.informacao?.id,
        produtoId: produto.id,
        codigo: produto.codigo,
        id: shortid.generate(),
      };
    });
    const mapProdutosRepetidos = produtosRepetidos.map((produto) => {
      const itemEncontrado = encontrarProduto(produto, produtosCadastrados);
      return {
        ...produto,
        informacao: itemEncontrado.informacao,
        produtoId: itemEncontrado.produtoId,
        produtoInformacaoId: itemEncontrado.produtoInformacaoId,
        codigo: itemEncontrado.codigo,
        id: shortid.generate(),
      };
    });
    produtosTemp = [
      ...produtosTemp,
      ...produtosCadastrados,
      ...mapProdutosRepetidos,
    ];
    setProdutos((prevState) =>
      ordenarPorOrdem([...prevState, ...produtosTemp]),
    );
    return notification.sucessoGenericos(
      "Cadastro de produtos realizado com sucesso!",
    );
  };

  const buscarMeiosPagamento = async (pagamentosDuplicatas) => {
    if (!pagamentosDuplicatas.length) return [];
    try {
      const nfePagamento = Array.from(
        new Set(pagamentosDuplicatas.map(({ tPag }) => tPag)),
      );
      const result = await meioPagamentoService.getAllFiltroAvancado({
        nfePagamento,
        nonPaginated: true,
      });
      if (!result.isAxiosError) {
        if (result.data.length) {
          return pagamentosDuplicatas
            .map((pagamentoDup) => {
              const meioPag = result.data.find(
                (meio) => meio.nfePagamento === pagamentoDup.tPag,
              );
              if (meioPag) {
                return {
                  id: shortid.generate(),
                  numeroTitulo: pagamentoDup.nDup,
                  dataVencimento: formatDate.toSend(
                    addDays(new Date(pagamentoDup.dVenc), 1),
                  ),
                  meioPagamentoId: meioPag.id,
                  valorTitulo: parseFloat(pagamentoDup.vDup),
                };
              }
              return null;
            })
            .filter((pagamento) => pagamento !== null);
        }
      } else {
        responseErros(result);
        throw new Error(
          result.response?.data?.message || "Erro ao buscar meios de pagamento",
        );
      }
    } catch (error) {
      throw new Error(error.message);
    }
  };

  async function finalizarImportacao() {
    try {
      await nfEntradaItensXmlExcetoUsoConsumoValidator.validate(produtos, {
        abortEarly: false,
      });
      const produtosTemp = produtos.map((item) => {
        const qtdTotal =
          (item.quantidade * 100 * (item?.conversorEntrada ?? 1)) / 100;
        const precoUnitario = item.subtotal / qtdTotal;
        const res = {
          ...item,
          precoUnitario,
          precoCompra: precoUnitario,
        };
        if (item?.informacao?.id) {
          res.informacaoId = item.informacao.id;
        }
        return res;
      });
      const nfEntradaDtoXml = new CadastroNfEntradaXml({
        ...dadosXml,
        itens: ordenarPorOrdem(produtosTemp),
        subtotalDesconto: calculaSubtotaisProdutos(produtos, "desconto"),
        subtotalAcrescimo: calculaSubtotaisProdutos(produtos, "acrescimo"),
        subtotalDespesas: calculaSubtotaisProdutos(produtos, "despesas"),
        pagamentos: await buscarMeiosPagamento(dadosXml.pagamentos),
        fornecedor,
        transportadora,
      });
      const nfEntradaDtoTemp = {
        ...nfEntradaDto,
        ...nfEntradaDtoXml,
      };
      setNfEntradaDto(nfEntradaDtoTemp);
      history.push("/estoque/nf-entrada/create");
    } catch (err) {
      inputErros.set(err);
      responseErros(err);
      throw new Error(
        err?.errors[0] ? `${err.errors[0]} é obrigatório` : "Erro de validação",
      );
    }
  }

  const handleInitStep = () => {
    setProdutos([]);
    setProdutosNaoCadastrados([]);
    setIsCadastro(false);
    setInputErro([]);
  };

  return (
    <>
      <Minimal
        title="importação xml"
        cardAction={
          <ImportarXmlActions
            steps={steps}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            handleInitStep={handleInitStep}
            dadosXml={dadosXml}
            isCadastro={isCadastro}
            setIsCadastro={setIsCadastro}
            setProdutos={setProdutos}
            produtosNaoCadastrados={produtosNaoCadastrados}
            handleCadastrarFornecedor={cadastrarFornecedor}
            handleCadastrarTransportadora={cadastrarTransportadora}
            setLoading={setLoading}
          />
        }
      >
        <Stepper activeStep={activeStep}>
          {steps.map(({ titulo }) => (
            <Step key={titulo}>
              <StepLabel>{titulo}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <ImportarXmlStepper
          activeStep={activeStep}
          arquivoXml={arquivoXml}
          setArquivoXml={setArquivoXml}
          fornecedor={fornecedor}
          transportadora={transportadora}
          produtos={produtos}
          setProdutos={setProdutos}
          produtosNaoCadastrados={produtosNaoCadastrados}
          setProdutosNaoCadastrados={setProdutosNaoCadastrados}
          setIsCadastro={setIsCadastro}
          setActiveStep={setActiveStep}
          inputErros={inputErros}
        />
      </Minimal>
      <Loading loading={loading} />
    </>
  );
};

export default ImportarXmlView;
